import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    DocUploadModal,
    i18n,
    LIPScreen,
    LoanStatusInfoCard,
    LoanStatusTrackerWeb,
    LoanSummaryRebrand,
    LoanTrackingTasks,
    NetTangibleBenefitCard
} from 'fmc-digital-originations';
import { auth, config } from '../../../config';
import { WidgetContentContainer, WidgetContentContainerBg } from '@app.styled';
import { NEW_LOAN_IN_PROGRESS_WIDGETS } from '@utils/appConstants';
import { dispatch } from '@store';
import { useSelector } from 'react-redux';
import { setESIGNDISCLOSEDACTIVEUSER, setESIGNPOPUPSTATUS, setParentModal } from '@store/actions/lip.action';
import { selectEsignPopUpState, selectSelctedDisclosureUser } from '@store/selectors/lip.selector';
import { ContextProvider, setAdditionalProperties } from 'fmc-digital-common';
import AlertModal from '@components/AlertModal/AlertModal';
import {
    selectHasServicingLoan,
    selectLoan,
    selectLoanNumberSelected,
    selectLoanStatusByLoanNumberV2,
    selectNLIPWidgetsList,
    selectSelectedLoanNumber
} from '@store/selectors/widgets.selectors';
import { CodingConstants } from '@constants';
import { setOfferTypeSelected } from '@store/actions/widgets.actions';
import { Identifier } from '@types';
import { Confetti } from '@components/Confetti/Confetti';
import { getLipNTBFeatureFlag } from '@store/reducers/config.reducer';
import { useLocation, useNavigate } from 'react-router-dom';
import { withHeaderFooter } from '../../HOCs';
import { NewLoanInProgressNavigationProps } from '../../data';
import { Container, ThemeProvider } from '@mui/material';
import { BoxBase, generateTheme, StackBase } from '@fmc-digital/ui';
import { I18nextProvider } from 'react-i18next';
import { isEmpty } from 'lodash';
import { AuthenticationLoader } from '@components_v2/UI/atoms/loading';
import { usePullToRefresh } from '@hooks/usePullToRefresh';
import { useVWOContext } from 'context/VWOContext';

export const ReSkinnedNewLoanInProgressContainer = withHeaderFooter(
    () => {
        const { RecordVWOAttributes } = useVWOContext();
        const navigate = useNavigate();
        const location = useLocation();
        const pathItems = location.pathname.split('/');
        const loanNumber = pathItems.at(pathItems.length - 1);
        const { state: routeState } = useLocation();
        const [showUploadModalWeb, setShowUploadModalWeb] = useState(false);
        const token = auth.getOkta().getAccessToken();
        const selfServUrl = config.env('selfServUrl');
        const xApiKey = config.env('xApiKey');
        const status: string = useSelector(selectEsignPopUpState);
        const selctedDisclosureUser = useSelector(selectSelctedDisclosureUser);
        const [isEsignUpdating, setEsignUpdating] = useState<boolean>(false);
        const hasServicingLoan: boolean = useSelector(selectHasServicingLoan);
        const loanOptions: Identifier[] = useSelector(selectLoan);
        const selectedLoanNumber = useSelector(selectSelectedLoanNumber);
        const [isConfettiNeeded, setIsConfettiNeeded] = useState(false);
        const isNtbRequired = useSelector(getLipNTBFeatureFlag);
        //new coding
        const nlpWidgetList = useSelector(selectNLIPWidgetsList);
        const loanStatus = useSelector(selectLoanStatusByLoanNumberV2);
        const widgets = useMemo(() => {
            return isEmpty(routeState)
                ? NEW_LOAN_IN_PROGRESS_WIDGETS.map(cWidgetName => {
                      return nlpWidgetList.includes(cWidgetName) ? cWidgetName : null;
                  }).filter(Boolean)
                : routeState;
        }, [nlpWidgetList, routeState]);

        const props2: any = {
            loanNumber,
            authToken: token,
            widgets,
            selfServUrl: selfServUrl,
            xApiKey: xApiKey,
            loanStatus,
            isNtbRequired
        };

        useEffect(() => {
            if (selectedLoanNumber?.propertyAddress) {
                const { city, state, zip } = selectedLoanNumber?.propertyAddress;
                RecordVWOAttributes({ propertyCity: city, propertyState: state, propertyZipCode: zip });
            }
            if (selectedLoanNumber?.loanType) {
                RecordVWOAttributes({ loanType: selectedLoanNumber?.loanType });
            }
        }, [RecordVWOAttributes, selectedLoanNumber]);

        useEffect(() => {
            if (loanStatus) {
                RecordVWOAttributes({ loanStatus: loanStatus });
            }
        }, [RecordVWOAttributes, loanStatus]);
        useEffect(() => {
            if (status === 'closed') {
                setEsignUpdating(true);
            }
        }, [status]);
        useEffect(() => {
            setAdditionalProperties('isNtbRequired', isNtbRequired);
            setAdditionalProperties('amplify', { loanNumber });
        }, [isNtbRequired, loanNumber]);
        useEffect(() => {
            setTimeout(() => {
                showRefreshLoaderStatus = false;
            }, 500);
        }, [isEsignUpdating]);
        const handleTokenResponse = useCallback((token: string, url: string, userName: string) => {
            dispatch(setParentModal({ showModal: true, component: 'esign', payload: { token, url } }));
            navigate('/e-sign');
            dispatch(setESIGNDISCLOSEDACTIVEUSER(userName));
            dispatch(setESIGNPOPUPSTATUS('opened'));
        }, []);

        const returnToHome = () => {
            dispatch(setOfferTypeSelected(undefined));
            const options = loanOptions?.filter(option => option.type == CodingConstants.servicingLoanType);
            if (options && options?.length > 0) {
                navigate('/', { preventScrollReset: true });
            }
        };

        const handleShowconfetti = useCallback(
            (status: string) => {
                if (status && status === 'Closing') {
                    if (isConfettiNeeded) {
                        setIsConfettiNeeded(false);
                    }
                    setTimeout(() => {
                        setIsConfettiNeeded(true);
                    }, 1000);
                }
            },
            [isConfettiNeeded]
        );

        setAdditionalProperties('selfServUrl', props2.selfServUrl);
        setAdditionalProperties('authToken', props2.authToken);
        setAdditionalProperties('xAPIKey', props2.xApiKey);
        setAdditionalProperties('amplify', { loanNumber });

        const isTaskSection: boolean = widgets?.includes('loan-in-progress-task') || false;
        const isStatusTrackerSection: boolean = widgets?.includes('status_tracker_widget') || false;
        const refreshContainerRef = useRef<HTMLInputElement>(null);
        let showRefreshLoaderStatus = usePullToRefresh({
            refreshContainerRef: refreshContainerRef
        });

        return (
            <I18nextProvider i18n={i18n}>
                <Container maxWidth="lg" sx={{ py: '1rem' }}>
                    {showRefreshLoaderStatus && (
                        <BoxBase
                            sx={theme => ({
                                display: 'flex',
                                paddingBottom: theme.spacing(2),
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '100%',
                                position: 'relative',
                                transition: 'margin-top 0.5s'
                            })}
                        >
                            <AuthenticationLoader />
                        </BoxBase>
                    )}
                    <StackBase gap={2} ref={refreshContainerRef}>
                        <StackBase gap={2} direction={{ md: 'column', lg: 'row' }}>
                            <NetTangibleBenefitCard isNtbRequired={props2.isNtbRequired} loanNumber={loanNumber} />
                            <LoanSummaryRebrand
                                url={selfServUrl}
                                authToken={props2.authToken}
                                loanNumber={props2.loanNumber}
                                loanStatus={loanStatus}
                                xApiKey={props2.xApiKey}
                            />
                        </StackBase>
                        {isStatusTrackerSection && (
                            <LoanStatusTrackerWeb
                                url={props2.url}
                                selfServUrl={props2.selfServUrl}
                                xApiKey={props2.xApiKey}
                                authToken={props2.authToken}
                                loanNumber={props2.loanNumber}
                            />
                        )}
                        <LoanStatusInfoCard
                            {...props2}
                            loanNumber={loanNumber}
                            widgets={widgets}
                            loanStatus={loanStatus}
                            onReturnToHome={returnToHome}
                            onGetInProgressStatus={handleShowconfetti}
                            isAllowReturnToHome={hasServicingLoan}
                        />
                        {isTaskSection && (
                            <LoanTrackingTasks
                                {...props2}
                                handleOnTokenResponse={handleTokenResponse}
                                setShowUploadModalWeb={setShowUploadModalWeb}
                                activeDisclosedUserName={selctedDisclosureUser}
                                isEsignUpdating={isEsignUpdating}
                                setEsignUpdating={setEsignUpdating}
                                isAllowReturnToHome={hasServicingLoan}
                                refreshingStatus={showRefreshLoaderStatus}
                            />
                        )}
                    </StackBase>
                </Container>
            </I18nextProvider>
        );
    },
    { ...NewLoanInProgressNavigationProps }
);
